// Design tokens for the public "Powered by DQ" marketing site.
//
// Revamp (Sep 2026): rebuilt to match the DQ Pro workspace/dashboard's actual
// product design system (src/utils/buildTheme.js DEFAULT_BRAND_COLORS, and a
// survey of PerformanceSummary.page.jsx / SolutionsOverview.page.jsx / the
// GrowthOpportunities dashboards — the product's system-of-record for its
// card/pill/badge conventions) rather than the original source mockup's own
// rust-orange editorial palette. Content stays word-for-word unchanged; only
// the visual language moves to the product's own colors, type scale and flat,
// pill-shaped, border-led component style.
export const pbdqColors = {
    bg: '#FFFBF7',      // app/page background — matches DEFAULT_BRAND_COLORS.background
    surface: '#FFFFFF', // card/panel background
    canvas: '#F9FAFB',  // subtle nested-tile fill inside cards
    text: '#111827',    // body / heading text — DEFAULT_BRAND_COLORS.text
    heading: '#111827',
    ink2: '#374151',
    grey: '#6B7280',    // muted text/labels

    navy: '#052F44',    // DEFAULT_BRAND_COLORS.secondary — headings/secondary accents

    brand: '#FF6900',
    accent: '#FF6900',        // DEFAULT_BRAND_COLORS.primary
    accent600: '#E55E00',     // the product's own button hover shade
    accent700: '#CC5200',
    accent200: 'rgba(255,105,0,0.08)', // icon-badge / soft pill tint
    accent100: '#FFF4EA',              // slightly stronger tint for larger surfaces
    accent900: '#7A2E00',

    divider: '#E5E7EB',
    neutral100: '#F9FAFB',
    neutral300: '#D1D5DB',

    blue: '#6A8EC9',
    blue600: '#3D5D8F',
    sky: '#3CC6F3',
    yellow: '#EEE921',
    lime: '#B7D334',
    gold: '#F8B143',
    magenta: '#C91C6E',
    pink: '#EA4E99',
    peri: '#6A8EC9',
    teal: '#3CC6F3',

    successDeep: '#166534',
    successBase: '#22C55E',
    successTint: 'rgba(22,101,52,0.12)',
    warningDeep: '#92400E',
    warningTint: 'rgba(239,160,32,0.15)',
    riskDeep: '#B91C1C',
    riskTint: 'rgba(224,69,53,0.12)',

    navBg: '#052F44',
    footerBg: '#052F44',
};

export const pbdqFont = "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// The site's 3-tier type scale for component/card-level content — a card or
// section's own title, its short bold subtitle line, and its body paragraph.
// Applied consistently everywhere that pattern appears, on both Home and
// Solutions. Does not cover the big page-level hero/section headings (h1,
// the major h2 section titles, the CTA band title) — those stay on their
// own larger, responsive (clamp-based) scale for page-level hierarchy.
export const pbdqTitleSize = 24;
export const pbdqSubtitleSize = 20;
export const pbdqParagraphSize = 20;

export const pbdqSpace = {
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
};

export const pbdqRadius = {
    sm: '8px',
    md: '12px',
    lg: '16px',
    pill: '999px',
};

// The product's own card treatment (PerformanceSummary.page.jsx et al.):
// a hairline border plus a whisper shadow — border-led, not shadow-led.
export const pbdqCardShadow = '0px 2px 8px rgba(0,0,0,0.04)';

// The horizontal page padding used throughout the source site (kept for the
// nav bar's own internal spacing — the content container itself is edge to
// edge, see pbdqShared.styles.js `container`/`footerInner`).
export const pbdqPageInline = 'clamp(20px, 5vw, 72px)';
export const pbdqMaxWidth = 1300;
